import { useEffect, useState } from "react";
import { useAsync } from "../useAsync.js";
import { Kpi } from "../Kpi.js";
import { api, type ImportReport } from "../api.js";

/** Row errors shown inline; the rest are counted, so a long list cannot bury the tiles. */
const ERRORS_SHOWN = 20;

export function ImportSubscribers({ org }: { org: string }) {
  const lists = useAsync(() => api.lists(org), [org]);
  const [listId, setListId] = useState("");
  const [csv, setCsv] = useState("");
  const [status, setStatus] = useState<"pending" | "confirmed">("pending");
  // Whether it was a dry run is remembered from the button that ran it, stored
  // WITH the report: the API does not echo the flag, so reading it off the
  // response titled every dry run "Import complete" (#343).
  const [result, setResult] = useState<{ report: ImportReport; dryRun: boolean } | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lists.data && lists.data.length > 0 && !listId) setListId(lists.data[0]!.listId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists.data]);

  const run = async (dryRun: boolean) => {
    setMsg(""); setResult(null); setBusy(true);
    try {
      setResult({ report: await api.importCsv(org, listId, csv, dryRun, status), dryRun });
    } catch (e) { setMsg(String(e)); }
    finally { setBusy(false); }
  };
  const valid = !!listId && csv.trim() !== "";

  return (
    <div>
      <h1 className="h1">Import subscribers · {org || "—"}</h1>
      <p className="muted" style={{ marginTop: -8 }}>
        Paste CSV to bulk-add subscribers to a list. Run a dry run first to preview counts.
      </p>
      {/*
        This screen sends the CSV inside the request, so it is bounded by the
        API's payload ceiling — a migration-sized file cannot go through here at
        all. Import (mapper) uploads the file straight to storage and runs it as
        a background job, so the operator who arrives here with a real export is
        told where to go rather than discovering it from a failed request (#287).
      */}
      <p className="muted" style={{ marginTop: -4 }}>
        For a file (rather than pasted text), a gzipped or JSON Lines export, or anything
        larger than a few megabytes, use <strong>Import (mapper)</strong> — it uploads
        directly to storage and runs as a background job.
      </p>
      {lists.data && lists.data.length === 0 && (
        <div className="card muted">No newsletters yet — create a list first.</div>
      )}
      <div className="card">
        <label>List</label>
        <select value={listId} onChange={(e) => setListId(e.target.value)} style={{ width: "100%" }}>
          {(lists.data ?? []).map((l) => (
            <option key={l.listId} value={l.listId}>{l.name} ({l.listId})</option>
          ))}
        </select>
        <label style={{ marginTop: 12 }}>Initial status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value as "pending" | "confirmed")} style={{ width: "100%" }}>
          <option value="pending">pending</option>
          <option value="confirmed">confirmed</option>
        </select>
        <label style={{ marginTop: 12 }}>CSV</label>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={10}
          placeholder={"email,first_name\nreader@example.com,Alex"}
          style={{ width: "100%", fontFamily: "monospace" }} disabled={busy} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
          <button className="btn ghost" disabled={!valid || busy} onClick={() => void run(true)}>Dry run</button>
          <button className="btn" disabled={!valid || busy} onClick={() => void run(false)}>{busy ? "Working…" : "Import"}</button>
          {msg && <span className="err">{msg}</span>}
        </div>
      </div>
      {result && (
        <div className="card">
          <div className="muted" style={{ marginBottom: 8 }}>{result.dryRun ? "Dry run — no changes applied" : "Import complete"}</div>
          <div className="kpis">
            <Kpi n={result.report.created} l="created" />
            <Kpi n={result.report.updated} l="updated" />
            <Kpi n={result.report.skipped} l="skipped" />
          </div>
          {/* The API folds these into one count; say what is in it (#343, #362). */}
          <div className="muted" style={{ marginTop: 8 }}>
            Skipped includes suppressed and duplicate addresses, and existing subscriptions an
            import never changes (unsubscribed or already confirmed). An existing subscriber can
            count as both updated and skipped: their details are updated, their subscription is not.
          </div>
          {/* Shown even when some rows succeeded — a partial failure must not look
              like the whole file went in (#343). */}
          {result.report.errors.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className="err">{result.report.errors.length} row(s) not imported</div>
              <ul>
                {result.report.errors.slice(0, ERRORS_SHOWN).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
              {result.report.errors.length > ERRORS_SHOWN && (
                <div className="muted">…and {result.report.errors.length - ERRORS_SHOWN} more.</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
